import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  isTruthy,
  openErrorNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";
import { getClosureReportDetails } from "./ShowAllService";
import { getTeamList } from "../../../MetaData/components/MetaList/components/AddTeamMetaList/CreateService";
import { applyLicenseKey } from "../../../../utils/License";
import YhTable, { YhTableColumn } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import YhSelect from "../../../../ui/YhSelect";
import { YhDateRangePicker } from "../../../../ui/YhDateTimePicker";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../../../redux/authSlice";

const MONTH_FIELDS = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
] as const;

const MONTH_LABELS: Record<string, string> = {
  Apr: "Apr",
  May: "May",
  Jun: "Jun",
  Jul: "Jul",
  Aug: "Aug",
  Sept: "Sep",
  Oct: "Oct",
  Nov: "Nov",
  Dec: "Dec",
  Jan: "Jan",
  Feb: "Feb",
  Mar: "Mar",
};

const MONTH_FULL: Record<string, string> = {
  Apr: "April",
  May: "May",
  Jun: "June",
  Jul: "July",
  Aug: "August",
  Sept: "September",
  Oct: "October",
  Nov: "November",
  Dec: "December",
  Jan: "January",
  Feb: "February",
  Mar: "March",
};

const TEAM_PALETTE = [
  "#841C39",
  "#0F766E",
  "#B45309",
  "#BE185D",
  "#0369A1",
  "#A02448",
  "#B91C1C",
  "#15803D",
];

const AVATAR_PALETTE = [
  "#841C39",
  "#0F766E",
  "#B45309",
  "#BE185D",
  "#0369A1",
  "#A02448",
  "#15803D",
  "#B45309",
];

const hashStr = (s: string) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return Math.abs(h);
};

const teamColor = (name: string) =>
  TEAM_PALETTE[hashStr(name || "x") % TEAM_PALETTE.length];

const avatarColor = (name: string) =>
  AVATAR_PALETTE[hashStr(name || "x") % AVATAR_PALETTE.length];

const initials = (name: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatCount = (value: any) => {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value ?? 0);
  return n.toLocaleString("en-IN");
};

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const MonthValueCell = ({ value }: { value: any }) => {
  const n = Number(value);
  if (!n || Number.isNaN(n)) {
    return <span className="block w-full text-center text-sm text-[#C4C9D4]">—</span>;
  }
  return (
    <span className="block w-full text-center text-sm font-semibold tabular-nums text-[#0F766E]">
      {n}
    </span>
  );
};

const SummaryRow = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}) => (
  <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 gap-y-0.5 border-b border-[#F1F3F9] py-2.5 last:border-b-0">
    <span className="text-[13px] leading-5 text-[#6B7280]">{label}</span>
    <span
      className={`max-w-[140px] text-right text-[13px] font-semibold leading-5 tabular-nums ${
        accent ? "text-[#841C39]" : "text-[#111827]"
      }`}
    >
      {value}
    </span>
  </div>
);

const ShowAll = (_props: any) => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<any>({
    from: null,
    to: null,
  });
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [offialDetailState, setOffialDetailState] = useState<any>({});
  const [teamdata, setTeamData] = useState<any[]>([]);
  const [headerData, setHeaderData] = useState<any>();
  const [metaData, setMetaData] = useState<any>({});
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [tableTeamId, setTableTeamId] = useState("");

  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    setOffialDetailState({});
    setTableTeamId("");
    loadTeams();
  }, [activeOrganizationId]);

  useEffect(() => {
    fetchRows();
  }, [
    selectedDateRange,
    paginationModel,
    offialDetailState?.team_id?.id,
    search,
    activeOrganizationId,
  ]);

  const loadTeams = async () => {
    try {
      const response3 = await getTeamList();
      const data3 = (response3?.data || []).flatMap((team: any) => {
        const entries: any[] = [];
        if (team.team_name) {
          entries.push({ id: team._id, label: team.team_name, isParent: true });
        }
        if (team.sub_teams && Array.isArray(team.sub_teams)) {
          team.sub_teams.forEach((subTeam: any) => {
            if (subTeam.team_name) {
              entries.push({
                id: subTeam._id,
                label: subTeam.team_name,
                isParent: false,
              });
            }
          });
        }
        return entries;
      });
      setTeamData(data3);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      const payload = {
        startDate: selectedDateRange?.from || "",
        endDate: selectedDateRange?.to || "",
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
        teamId: offialDetailState?.team_id?.id || "",
        search: search || "",
        usersId: "",
      };

      const response = await getClosureReportDetails(payload);
      const totals = response?.dataTotalCount;
      setHeaderData(Array.isArray(totals) ? totals[0] : totals);
      const meta = response?.metadata ?? response?.metaData;
      setMetaData(
        Array.isArray(meta) && meta[0]
          ? meta[0]
          : meta && !Array.isArray(meta)
            ? meta
            : {}
      );

      const dt = Array.isArray(response?.data) ? response.data : [];
      const source = dt.map((userData: any, index: number) => {
        const monthValues = MONTH_FIELDS.reduce((acc: any, month) => {
          acc[month] = userData?.[month] ?? 0;
          return acc;
        }, {});

        const numericTotal = MONTH_FIELDS.reduce(
          (acc, month) => acc + (Number(userData?.[month]) || 0),
          0
        );

        return {
          ...userData,
          id: userData._id || userData.id || `row-${index}`,
          team_name: userData.team_name ?? "",
          user_name: userData.user_name ?? "",
          ...monthValues,
          grand_total: userData.grand_total ?? numericTotal,
        };
      });
      setRows(source);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setRows([]);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleTeamChange = (teamId: string, syncTable = true) => {
    if (!teamId) {
      setOffialDetailState((prev: any) => ({
        ...prev,
        team_id: { error: "", value: "", id: null },
      }));
      if (syncTable) setTableTeamId("");
      return;
    }
    const opt = teamdata.find((t: any) => String(t.id) === String(teamId));
    setOffialDetailState((prev: any) => ({
      ...prev,
      team_id: {
        error: "",
        value: opt?.label || "",
        id: opt?.id ?? teamId,
      },
    }));
    if (syncTable) setTableTeamId(String(teamId));
  };

  const tableTeamOptions = useMemo(
    () =>
      (teamdata || []).map((t: any) => ({
        label: t.isParent ? t.label : `  ${t.label}`,
        value: String(t.id),
      })),
    [teamdata]
  );

  const displayRows = useMemo(() => {
    let list = rows;
    if (tableTeamId && !offialDetailState?.team_id?.id) {
      list = list.filter(
        (r) =>
          String(r.team_id) === String(tableTeamId) ||
          String(r.teamId) === String(tableTeamId)
      );
    }
    return list;
  }, [rows, tableTeamId, offialDetailState?.team_id?.id]);

  const monthlyTrendData = useMemo(
    () =>
      MONTH_FIELDS.map((m) => ({
        month: MONTH_LABELS[m],
        key: m,
        value: Number(headerData?.[m] ?? 0),
      })),
    [headerData]
  );

  const summary = useMemo(() => {
    const total = Number(headerData?.total_count ?? 0);
    const avg = total / 12;

    const monthEntries = MONTH_FIELDS.map((m) => ({
      key: m,
      label: MONTH_FULL[m],
      value: Number(headerData?.[m] ?? 0),
    }));
    const maxVal = Math.max(...monthEntries.map((m) => m.value), 0);
    const highestMonths =
      maxVal > 0
        ? monthEntries
            .filter((m) => m.value === maxVal)
            .map((m) => m.label)
            .join(", ")
        : "—";

    const teamTotals: Record<string, number> = {};
    rows.forEach((r) => {
      const name = r.team_name || "Unknown";
      teamTotals[name] =
        (teamTotals[name] || 0) + (Number(r.grand_total) || 0);
    });
    const ranked = Object.entries(teamTotals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    const best = ranked[0]?.name || "—";
    const topMax = ranked[0]?.value || 1;
    const pieTotal = ranked.reduce((s, t) => s + t.value, 0) || 1;

    return {
      total,
      avg,
      highestMonths,
      best,
      ranked,
      topMax,
      pieData: ranked.slice(0, 6).map((t) => ({
        name: t.name,
        value: t.value,
        pct: ((t.value / pieTotal) * 100).toFixed(1),
        color: teamColor(t.name),
      })),
    };
  }, [headerData, rows]);

  const maxMonthValue = useMemo(
    () => Math.max(...monthlyTrendData.map((d) => d.value), 0),
    [monthlyTrendData]
  );

  const columns: YhTableColumn[] = useMemo(
    () => [
      {
        field: "user_name",
        headerName: "Employee",
        minWidth: 200,
        flex: 1.2,
        renderCell: ({ value }) => (
          <div className="flex items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: avatarColor(String(value || "")) }}
            >
              {initials(String(value || ""))}
            </span>
            <span className="truncate text-sm font-medium text-[#111827]">
              {value || "—"}
            </span>
          </div>
        ),
      },
      {
        field: "team_name",
        headerName: "Team",
        minWidth: 140,
        flex: 1,
        renderCell: ({ value }) => (
          <span
            className="truncate text-sm font-medium"
            style={{ color: teamColor(String(value || "")) }}
          >
            {value || "—"}
          </span>
        ),
      },
      ...MONTH_FIELDS.map((field) => ({
        field,
        headerName: (
          <span className="block w-full text-center">
            {MONTH_LABELS[field].toUpperCase()}
          </span>
        ),
        minWidth: 64,
        width: 72,
        renderCell: ({ value }: any) => <MonthValueCell value={value} />,
      })),
      {
        field: "grand_total",
        headerName: (
          <span className="block w-full text-right">Total</span>
        ),
        minWidth: 88,
        renderCell: ({ value }) => (
          <span className="block w-full text-right text-sm font-semibold tabular-nums text-[#111827]">
            {formatCount(value)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <div className="mb-5 shrink-0">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
          Closure Report
        </h1>
        <p className="mt-1 text-sm text-[#6B7280]">
          Fiscal year closures by employee and team (Apr–Mar).
        </p>
      </div>

      {/* Metrics: total + months + trend */}
      <div className="mb-4 grid shrink-0 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)]">
        <section className="overflow-hidden rounded-2xl border border-[#E8EAF0] bg-white">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[#E8EAF0] bg-[linear-gradient(135deg,#F8F7FF_0%,#FFFFFF_55%)] px-5 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
                Grand total
              </p>
              <p className="mt-1 text-4xl font-bold tabular-nums tracking-tight text-[#111827]">
                {headerData ? formatCount(headerData.total_count) : "—"}
              </p>
              <p className="mt-1 text-sm text-[#6B7280]">Closures this fiscal year</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#9CA3AF]">Avg / month</p>
              <p className="text-lg font-semibold tabular-nums text-[#841C39]">
                {headerData ? summary.avg.toFixed(1) : "—"}
              </p>
            </div>
          </div>

          <div className="px-3 py-3 sm:px-4">
            <div className="grid grid-cols-4 gap-px overflow-hidden rounded-xl border border-[#EEF0F5] bg-[#EEF0F5] sm:grid-cols-6 lg:grid-cols-12">
              {MONTH_FIELDS.map((m) => {
                const value = Number(headerData?.[m] ?? 0);
                const active = value > 0;
                const intensity =
                  maxMonthValue > 0 ? Math.min(1, value / maxMonthValue) : 0;
                return (
                  <div
                    key={m}
                    className={`relative flex flex-col items-center justify-center gap-1 bg-white px-1 py-3 ${
                      active ? "" : "opacity-70"
                    }`}
                    title={`${MONTH_FULL[m]}: ${formatCount(value)}`}
                  >
                    {active ? (
                      <span
                        className="absolute inset-x-0 bottom-0 h-0.5 bg-[#841C39]"
                        style={{ opacity: 0.35 + intensity * 0.65 }}
                        aria-hidden
                      />
                    ) : null}
                    <span className="text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
                      {MONTH_LABELS[m]}
                    </span>
                    <span
                      className={`text-base font-bold tabular-nums leading-none ${
                        active ? "text-[#111827]" : "text-[#D1D5DB]"
                      }`}
                    >
                      {headerData ? formatCount(value) : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="flex min-h-[220px] flex-col rounded-2xl border border-[#E8EAF0] bg-white p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-[#111827]">
                Monthly closure trend
              </p>
              <p className="text-xs text-[#9CA3AF]">Apr → Mar</p>
            </div>
            <span className="rounded-md bg-[#F3F4F6] px-2 py-1 text-[11px] font-medium text-[#6B7280]">
              This year
            </span>
          </div>
          <div className="min-h-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monthlyTrendData}
                barSize={14}
                margin={{ top: 8, right: 4, left: -12, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#EEF0F5"
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "#9CA3AF" }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "#9CA3AF" }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <Tooltip
                  cursor={{ fill: "#F8F7FF" }}
                  contentStyle={{
                    borderRadius: 10,
                    border: "1px solid #EEF0F5",
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                  {monthlyTrendData.map((entry) => (
                    <Cell
                      key={entry.key}
                      fill={entry.value > 0 ? "#841C39" : "#E5E7EB"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      {/* Table + sidebar */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden xl:grid-cols-[minmax(0,1fr)_260px]">
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <div className="mb-3 grid shrink-0 grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,1fr)_200px_220px] sm:items-end">
            <YhInput
              label="Search"
              placeholder="Search by name or team…"
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              autoFocus={isSearchFocused}
              startAdornment={<SearchSvg />}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
            />
            <YhSelect
              label="Team"
              placeholder="All teams"
              searchPlaceholder="Search team…"
              showOptionIcon={false}
              clearable
              value={tableTeamId}
              options={tableTeamOptions}
              onChange={(e) => {
                const v = String(e.target.value || "");
                setTableTeamId(v);
                handleTeamChange(v, false);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            />
            <div className="w-full">
              <label className="mb-1.5 block text-sm font-medium text-[#374151]">
                Date range
              </label>
              <YhDateRangePicker
                className="w-full"
                placeholder="MM/DD/YYYY – MM/DD/YYYY"
                value={{
                  from: selectedDateRange?.from || null,
                  to: selectedDateRange?.to || null,
                }}
                onChange={(range) => {
                  setSelectedDateRange(range);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-[#E8EAF0] bg-white">
            <YhTable
              columns={columns}
              rows={displayRows}
              getRowId={(row) => row.id || row._id}
              rowHeight={56}
              loading={loading}
              emptyMessage="No report rows yet"
              className="!h-full !min-h-0 !border-0 !shadow-none"
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              totalRows={metaData?.total || displayRows.length || 0}
              paginationLabel="employees"
              onPageChange={(p) =>
                setPaginationModel((prev) => ({ ...prev, page: p }))
              }
              onPageSizeChange={(s) =>
                setPaginationModel({ pageSize: s, page: 0 })
              }
            />
          </div>
        </div>

        <aside className="flex max-h-[480px] min-h-0 flex-col gap-3 overflow-y-auto xl:max-h-none">
          <section className="rounded-2xl border border-[#E8EAF0] bg-white p-4">
            <p className="mb-1 text-sm font-semibold text-[#111827]">Summary</p>
            <p className="mb-2 text-xs text-[#9CA3AF]">Key figures for the current view</p>
            <SummaryRow label="Best team" value={summary.best} accent />
            <SummaryRow label="Total closures" value={formatCount(summary.total)} />
            <SummaryRow
              label="Monthly average"
              value={summary.avg.toFixed(2)}
            />
            <SummaryRow
              label="Highest month"
              value={summary.highestMonths}
              accent
            />
          </section>

          <section className="rounded-2xl border border-[#E8EAF0] bg-white p-4">
            <p className="mb-3 text-sm font-semibold text-[#111827]">
              Top teams
            </p>
            {summary.ranked.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">No team data</p>
            ) : (
              <div className="space-y-3">
                {summary.ranked.slice(0, 5).map((t, idx) => (
                  <div key={t.name}>
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#F3F4F6] text-[10px] font-bold text-[#6B7280]">
                          {idx + 1}
                        </span>
                        <span className="truncate text-xs font-medium text-[#374151]">
                          {t.name}
                        </span>
                      </div>
                      <span className="shrink-0 text-xs font-semibold tabular-nums text-[#111827]">
                        {t.value}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-[#F3F4F6]">
                      <div
                        className="h-full rounded-full bg-[#841C39]"
                        style={{
                          width: `${Math.max(
                            6,
                            (t.value / Math.max(summary.topMax, 1)) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-[#E8EAF0] bg-white p-4">
            <p className="mb-3 text-sm font-semibold text-[#111827]">
              Distribution
            </p>
            {summary.pieData.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">No distribution data</p>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="mx-auto h-[128px] w-[128px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={summary.pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={36}
                        outerRadius={54}
                        paddingAngle={2}
                      >
                        {summary.pieData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          borderRadius: 10,
                          border: "1px solid #EEF0F5",
                          fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="space-y-2">
                  {summary.pieData.map((entry) => (
                    <li
                      key={entry.name}
                      className="grid grid-cols-[1fr_auto] items-center gap-2"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="truncate text-xs text-[#6B7280]">
                          {entry.name}
                        </span>
                      </div>
                      <span className="text-xs font-semibold tabular-nums text-[#111827]">
                        {entry.pct}%
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
};

export default ShowAll;
